import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid, StageDivider } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Consensus: Proof of Stake',
    tagline: 'How validators agree on one canonical chain without mining.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what a validator stakes and what activates that stake
//   Q1 - the finality threshold (roughly two-thirds of stake)
//   Q2 - what is actually slashable vs. merely a missed-reward penalty
export const blocks: StageBlock[] = [
    content('validators', () => (
        <StageSection gradientBorder eyebrow="Stage 6 of 8" title="Staking ETH instead of burning electricity">
            <Prose>
                Since the Merge, Ethereum no longer selects block producers through proof-of-work mining. Instead,
                anyone can become a <strong>validator</strong> by depositing 32 ETH into the deposit contract. That
                stake is both the validator's ticket to participate and its collateral: behave honestly and it earns
                rewards, behave provably dishonestly and part of it can be destroyed.
            </Prose>
            <FactGrid items={[
                ['Slot', 'A fixed 12-second window in which one validator, chosen pseudorandomly, is the proposer with the right to publish a block.'],
                ['Epoch', 'A group of 32 consecutive slots. Validators are periodically shuffled into committees for each epoch.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('attestations', () => (
        <StageSection title="Attesting: the other validators keep the proposer honest">
            <Prose>
                Every validator not proposing in a given slot is still expected to <strong>attest</strong>: to
                cast a vote for the block (or chain head) it believes is correct. These attestations are what let
                the network measure, in aggregate, how much staked ETH agrees on a given version of history.
            </Prose>
            <Prose>
                A single proposer having a bad block accepted doesn't finalize anything by itself; it takes a
                supermajority of attesting stake, sustained across two consecutive epochs, before a checkpoint is
                considered <strong>finalized</strong>.
            </Prose>
        </StageSection>
    )),

    content('finality', () => (
        <StageSection title="Finality: when a block becomes economically final">
            <Prose>
                Ethereum's finality rule requires roughly two-thirds of all staked ETH to attest to the same
                checkpoint before it's finalized. Once finalized, reverting that block would require validators
                holding at least one-third of all stake to provably violate the protocol's slashing rules, an
                action so expensive it's treated as effectively irreversible in practice.
            </Prose>
            <Prose>
                Blocks can still be produced and built upon before finality is reached; finality is a stronger,
                slower-arriving guarantee layered on top of ordinary block production, not a requirement for the
                chain to keep moving.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('slashing', () => (
        <StageSection title="Slashing targets provable misbehavior, not bad luck">
            <Prose>
                Going offline costs a validator missed rewards, a small, non-slashable penalty for simply not
                doing your job. Slashing is reserved for actions that are cryptographically provable and
                unambiguously dangerous to consensus:
            </Prose>
            <KeyPoints items={[
                <>Proposing two different blocks for the same slot.</>,
                <>Attesting to two conflicting checkpoints in a way that violates the protocol's voting rules (a "surround" or "double" vote).</>,
            ]} />
            <StageDivider />
            <Prose>
                Both are the kind of behavior that, if done at scale, could let an attacker rewrite finalized
                history, so the protocol makes them detectable on-chain and punishes them by destroying part of the
                offending validator's stake and forcibly ejecting it.
            </Prose>
        </StageSection>
    )),

    question(2),
];
